<template>
  <div class="matrix-edit-table w-full flex flex-col gap-4">
    <Button
      v-if="!readonly"
      class="add-new__save-btn flex-shrink-0 w-32 ml-auto"
      type="primary"
      :disabled="disabled || isAnyEditing || isValuesLoading || loading"
      @click="addNewRow"
    >
      {{ $t('actions.add_new') }}
    </Button>

    <Loader v-if="isValuesLoading" />

    <Table
      v-else
      id="riskTable"
      mode="records"
      :key="tableKey + filteredList.length"
      :columns="columns"
      :rows="filteredList"
      :total-rows="filteredList.length"
      :filters="filters"
      :filterOptions="filterOptions"
      :isLoading="loading"
      @column-filter="onColumnFilters"
      @filters-clear="resetFilters"
    >
      <template #link="customData">
        <div class="truncate max-w-full whitespace-nowrap cursor-pointer">
          <span v-tippy :content="customData.customData.name">
            {{ customData.customData.name }}
          </span>
        </div>
      </template>
      <template #actions="{customData}">
        <div class="flex justify-around">
          <SaveButton
            v-if="getRelatedConfig(customData)?.isEditing"
            :disabled="disabled || getRelatedConfig(customData)?.isDisabled"
            @click="saveRow(customData)"
          />
          <EditButton
            v-else
            :disabled="customData.disabled || disabled"
            @click="enableEditing(customData)"
          />
          <CloneButton
            :disabled="disabled || matrixData.isAnyEditing"
            @click="cloneRow(customData)"
          />
          <DeleteButton
            :disabled="disabled || (matrixData.isAnyEditing && !getRelatedConfig(customData)?.isEditing)"
            @click="deleteRow(customData)"
          />
        </div>
      </template>

      <template #clients-field="{ customData }">
        <FormulateInput
          class="client-multiselect"
          :value="getRelatedConfigData(customData)?.clients"
          :placeholder="$t('wam.client')"
          :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
          :options="clientOptions"
          :disabled="readonly || !getRelatedConfig(customData)?.isEditing"
          v-bind="dropdownConfig"
          @input="setConfigValue(customData, 'clients', $event)"
        />
      </template>

      <template #configuration-field="{ customData }">
        <FormulateInput
          class="configuration-multiselect"
          :key="getRelatedConfigData(customData)?.configuration?.id"
          :multiple="false"
          :value="getOptionsByValue(getRelatedConfigData(customData)?.configuration, fieldOptions)"
          :placeholder="$t('wam.configuration')"
          :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
          :options="fieldOptions"
          :disabled="readonly || !getRelatedConfig(customData)?.isEditing"
          v-bind="dropdownConfig"
          @input="setConfigValue(customData, 'configuration', $event)"
        />
      </template>

      <template #values-field="{ customData }">
        <FormulateInput
          class="values-multiselect"
          :key="getRelatedConfigData(customData)?.configuration?.id"
          :multiple="getRelatedConfigurationProp(customData)?.isMulti"
          :value="getOptionsByValue(getRelatedConfigurationProp(customData)?.getMultiselectValue, getRelatedConfigurationProp(customData)?.options || [])"
          :placeholder="$t('wam.value')"
          :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
          :options="getRelatedConfigurationProp(customData)?.options || []"
          v-bind="dropdownConfig"
          :config="{
            label: 'option_label',
            trackBy: 'id',
          }"
          :loading="getRelatedConfigurationProp(customData)?.isLoading"
          :disabled="readonly || !getRelatedConfigData(customData)?.configuration?.id  || !getRelatedConfig(customData)?.isEditing || getRelatedConfigurationProp(customData)?.isLoading"
          
          @input="setConfigValue(customData, 'values', $event)"
        />
      </template>

      <template #users-field="{ customData }">
        <FormulateInput
          class="user-multiselect"
          :value="getRelatedConfigData(customData)?.users"
          :placeholder="$t('wam.user')"
          :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
          :options="userOptions"
          :disabled="readonly || !getRelatedConfig(customData)?.isEditing"
          v-bind="dropdownConfig"
          @input="setConfigValue(customData, 'users', $event)"
        />
      </template>

      <template #groups-field="{ customData }">
        <FormulateInput
          class="groups-multiselect"
          :value="getRelatedConfigData(customData)?.groups"
          :placeholder="$t('wam.group')"
          :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
          :options="groupOptions"
          :disabled="readonly || !getRelatedConfig(customData)?.isEditing"
          v-bind="dropdownConfig"
          @input="setConfigValue(customData, 'groups', $event)"
        />
      </template>
      
      <template #action-field="{ customData }">
        <FormulateInput
          class="action-multiselect"
          :multiple="false"
          :value="getOptionsByValue(getRelatedConfigData(customData)?.action, actionOptions)"
          :placeholder="$t('wam.allocation')"
          :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
          :options="actionOptions"
          :disabled="readonly || !getRelatedConfig(customData)?.isEditing"
          v-bind="dropdownConfig"
          @input="setConfigValue(customData, 'action', $event)"
        />
      </template>
    </Table>

    <ModalConfirm ref="confirm-popup" />
  </div>
</template>

<script>
import { uuid } from "vue-uuid";
import axios from "@/axios";
import { mapGetters } from "vuex";
import Table from "@shared/dashboard-components/table-large";
import { getFilterOptions, getFilteredRows } from "@shared/dashboard-components/table-large/utils";
import { userType } from "@shared/utils/constants";

import Button from "@shared/components/button";
import Loader from "@shared/components/WrappedLoader";
import ModalConfirm from "@shared/modal-confirm";
import {
  CloneButton,
  EditButton,
  DeleteButton,
  SaveButton,
} from "@shared/components/icon-buttons";

import handleError from "@shared/mixins/handleError";

import {
  MatrixData,
  MatrixConfig,
  MatrixConfigurationProp,
} from '../../utils/MatrixData';
import { ACTION_OPTIONS } from '../../utils/constants';
import { checkIsMulti } from '../../utils';
import { getFieldValue } from "./utils";

const arrayFilterCheckCustom = (entityValue, filterValue) => !!entityValue?.some((item) => item.name == filterValue?.value);

const objectFilterCheckCustom = (entityValue, filterValue) => entityValue.options?.some((item) => item.option_label == filterValue?.value);


export default {
  mixins: [handleError],

  components: {
    Table,
    Button,
    Loader,
    CloneButton,
    EditButton,
    DeleteButton,
    SaveButton,
    ModalConfirm,
  },

  props: {
    matrixData: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tableKey: uuid.v4(),
      filters: {},
      isLoading: false,
      isValuesLoading: false,
      clientOptions: [],
      userOptions: [],
      groupOptions: [],
      fieldTypesWithOptions: [], // List of field types with options
      fieldOptions: [], // List of field variants for configuration value
      configurationOptionsMap: {}, // Map to store field options by field type ID
    };
  },

  computed: {
    ...mapGetters({
      getUserType: "getUserType",
      getTenantId: "getTenantId",
      getClientData: "getClientData",
    }),

    isClient() {
      return this.getUserType == userType.CLIENT;
    },
    isValidMatrix() {
      return !!(this.matrixData && this.matrixData instanceof MatrixData);
    },
    matrixConfig() {
      return this.isValidMatrix ? this.matrixData.data?.config : [];
    },

    // TODO translate
    columns() {
      return [
        {
          field: "clients",
          config: {
            filter: {
              customCheck: arrayFilterCheckCustom,
            },
            type: "tooltip_text",
            template: "clients-field",
          },
          tdClass: "matrix-edit-table--dropdown",
          label: this.$t("wam.client"),
          hide: this.isClient,
        },
        {
          field: "configuration",
          config: {
            filter: {
              optionsExtractor: (row) => row.configuration.options.map(opt => ({ name: opt.option_label })),
              customCheck: objectFilterCheckCustom,
            },
            type: "tooltip_text",
            template: "configuration-field",
          },
          tdClass: "matrix-edit-table--dropdown",
          label: this.$t("wam.configuration"),
        },
        {
          field: "values",
          config: {
            filter: {
              customCheck: arrayFilterCheckCustom,
            },
            type: "tooltip_text",
            template: "values-field",
          },
          tdClass: "matrix-edit-table--dropdown",
          label: this.$t("wam.value"),
        },
        {
          field: "users",
          config: {
            filter: {
              customCheck: arrayFilterCheckCustom,
            },
            type: "tooltip_text",
            template: "users-field",
          },
          tdClass: "matrix-edit-table--dropdown",
          label: this.$t("wam.user"),
        },
        {
          field: "groups",
          config: {
            filter: {
              customCheck: arrayFilterCheckCustom,
            },
            type: "tooltip_text",
            template: "groups-field",
          },
          tdClass: "matrix-edit-table--dropdown",
          label: this.$t("wam.group"),
        },
        {
          field: "action",
          config: {
            filter: true,
            type: "tooltip_text",
            template: "action-field",
          },
          tdClass: "matrix-edit-table--dropdown",
          label: this.$t("wam.allocation"),
        },
        {
          field: "actions",
          config: { filter: false, type: "actions" },
          tdClass: "matrix-edit-table--dropdown",
          width: "140px",
          sortable: false,
          label: this.$t("general.actions"),
          hide: this.readonly,
        },
      ].filter((el) => !el.hide);
    },
    filteredList() {
      return getFilteredRows(this.matrixConfig, this.filters, (row) => row.data);
    },
    filterOptions() {
      return getFilterOptions(this.columns, this.filteredList, (row) => row.data)
    },

    isAnyEditing() {
      if (!this.isValidMatrix) return false;
      return this.matrixData.isAnyEditing;
    },

    actionOptions() {
      return ACTION_OPTIONS.map((el) => ({
        id: el.id,
        name: this.$t(el.tKey),
      }));
    },

    dropdownConfig() {
      return {
        validation: "required",
        type: "multi-select",
        variant: "alt",
        searchable: true,
        showIcon: true,
        config: {
          label: "name",
          trackBy: "id",
        },
        disableErrors: true,
      };
    },
  },

  async mounted() {
    if (!this.isValidMatrix) return;
    this.isLoading = true;

    try {
      await this.initOptions();
    } catch (error) {
      this.handleError(error);
    }

    this.isLoading = false;
  },

  methods: {
    prefillClientData(matrixConfig) {
      if (!this.isValidMatrix || !this.getClientData) return;

      const relatedConfig = this.getRelatedConfig(matrixConfig);

      // Prefill client data if available
      const clientData = this.getClientData;
      relatedConfig.setValue("clients", [{
        id: clientData.id,
        name: clientData.name,
      }]);
    },
    updateTableKey() {
      this.tableKey = uuid.v4();
    },
    getRelatedConfig(customData) {
      if (!this.isValidMatrix) return null;

      return this.matrixConfig.find(config => config?.data?.id === customData?.data?.id);
    },
    getRelatedConfigData(customData) {
      if (!this.isValidMatrix) return null;

      const relatedConfig = this.getRelatedConfig(customData);
      if (!relatedConfig) return null;

      return relatedConfig.getData;
    },
    getRelatedConfigurationProp(customData) {
      if (!this.isValidMatrix) return null;

      const relatedConfig = this.getRelatedConfig(customData);

      return relatedConfig?.data?.configuration || null;
    },
    async onConfigurationValueSet(matrixConfig, field) {
      if (!this.isValidMatrix || !matrixConfig) return;

      const fieldId = field?.id;

      if (!fieldId) {
        matrixConfig.setValue("configuration", null);
        return;
      }

      // Update the configuration value in the matrix config
      const newConfigProp = new MatrixConfigurationProp({
        id: fieldId,
      });
      newConfigProp.setIsMulti(checkIsMulti(this.fieldTypesWithOptions, field));

      matrixConfig.setValue("configuration", newConfigProp)

      // Fetch options for the field type if not already fetched
      const options = this.configurationOptionsMap[fieldId];

      if (!options) {
        const fetchOptionsMethod = async () => await this.fetchFieldOptions(fieldId);
        await newConfigProp.fetchOptions(fetchOptionsMethod);
      } else {
        newConfigProp.setOptions(options);
      }
    },
    onConfigurationValuesValueSet(matrixConfig, value) {
      if (!this.isValidMatrix || !matrixConfig) return;

      const relatedConfigurationProp = this.getRelatedConfigurationProp(matrixConfig);
      if (!relatedConfigurationProp) return;

      relatedConfigurationProp.setValue(getFieldValue(value, "configurationValue") || []);
    },
    getConfigFieldValue(customData, field) {
      if (!this.isValidMatrix) return null;

      const relatedConfig = this.getRelatedConfig(customData);
      const relatedProp = relatedConfig?.data?.[field];

      if (!relatedProp) return null;
      
      const getValueMap = {
        values: () => {
          if (!(relatedConfig instanceof MatrixConfig)) return null;
          return relatedConfig.data?.configuration?.getMultiselectValue || null;
        }
      }

      return getValueMap[field]?.() || relatedProp;
    },
    async setConfigValue(customData, field, value) {
      const currentFieldValue = this.getConfigFieldValue(customData, field);
      // Validate the value before proceeding: init Formulate Input value is false
      // Currently, this comparison works properly only for the nullish values
      const isValueChanged = currentFieldValue !== value;

      if (!this.isValidMatrix || value === false || !isValueChanged) return;

      this.matrixData.touchConfig(); // Mark as changed
      const relatedConfig = this.getRelatedConfig(customData);

      if (relatedConfig) {
        relatedConfig.setEditing(true);
      }

      const fieldSpecificMethods = {
        configuration: this.onConfigurationValueSet,
        values: this.onConfigurationValuesValueSet
      }
      if (fieldSpecificMethods[field]) {
        await fieldSpecificMethods[field](relatedConfig, value);
      } else {
        relatedConfig.setValue(field, getFieldValue(value, field));
      }
    },

    onColumnFilters({ columnFilters }) {
      this.filters = columnFilters;
    },
    resetFilters() {
      this.filters = {};
    },
    saveRow(sourceData) {
      const relatedConfig = this.getRelatedConfig(sourceData);

      const checkErrors = this.matrixData.checkConfigErrors(relatedConfig);
      const failedCheck = checkErrors?.[0];
      if (failedCheck) {
        this.$toast.error(this.$t(failedCheck.tKey) || failedCheck.message);
        return;
      }

      if (this.disabled || !relatedConfig) return;

      if (relatedConfig.isValid) {
        relatedConfig.setEditing(false);
      }
    },
    enableEditing(sourceData) {
      const relatedConfig = this.getRelatedConfig(sourceData);
      if (this.disabled || !relatedConfig) return;
      if (relatedConfig) {
        relatedConfig.setEditing();
      }
    },
    cloneRow(sourceData) {
      const relatedConfig = this.getRelatedConfig(sourceData);
      const relatedConfigurationProp = this.getRelatedConfigurationProp(sourceData);
      if (this.disabled || !relatedConfig) return;

      const newConfig = this.addNewRow(relatedConfig.getData, true);
      const newConfigurationProp = this.getRelatedConfigurationProp(newConfig);
      // Copy properties from the related configuration prop
      if (newConfigurationProp) {
        newConfigurationProp.setIsMulti(!!relatedConfigurationProp?.isMulti);
        newConfigurationProp.setOptions(relatedConfigurationProp?.options || []);
      }
    },
    async deleteRow(sourceData) {
      if (!this.isValidMatrix) return;

      const relatedConfig = this.getRelatedConfig(sourceData);
      if (!relatedConfig) return;

      const isConfirmed = await this.$refs["confirm-popup"].show({
        message: this.$t('components.are_you_sure_you_want_to_delete'),
      });

      if (isConfirmed) {
        this.matrixData.removeConfiguration(relatedConfig.data?.id);
      }
    },
    addNewRow(sourceMatrixData = null, clone = false) {
      if (!this.isValidMatrix) return;

      const newConfig = this.matrixData.addNewConfiguration(sourceMatrixData, clone);
      this.prefillClientData(newConfig);
      return newConfig;
    },
    
    /* OPTIONS */
    async fetchClientList() {
      const url = "tenant/clients";
      const payload = {
        tenant_id: this.getTenantId,
        page: 1,
        count: 10000, // TODO
      }
      const { data } = await axios.post(url, payload);
      this.clientOptions = data.data.map(el => ({
        id: el.id,
        name: el.name,
      }));
    },
    async fetchFieldTypes() {
      const url = "fields/types";
      const { data } = await axios.get(url);
      this.fieldTypesWithOptions = data?.field_types.filter(type => type.has_options && type.visible !== false);
    },
    async getFieldsWithOptions() {
      await this.fetchFieldTypes();
      const typeList = this.fieldTypesWithOptions.map(type => type.id);
      const url = `fields/${this.getTenantId}/all_fields`;
      const payload = {
        page: 1,
        count: 10000, // TODO
      }

      const [ ...fields ] = await Promise.all(
        typeList.map(typeId => axios.get(url, {
          params: {
            ...payload,
            field_type: typeId
          },
        }))
      );

      this.fieldOptions = fields.flatMap(el => el.data?.fields || []);
    },
    async fetchFieldOptions(fieldId) {
      const url = `fields/${fieldId}/all_options`;
      const { data } = await axios.get(url);
      const options = data?.options || [];
      this.configurationOptionsMap[fieldId] = options;
      return options;
    },
    async fetchUserList() {
      const url = `user/tenant/${this.getTenantId}`;
      const { data } = await axios.get(url);
      this.userOptions = data.map(el => ({
        id: el.id,
        name: el.user_name,
        email: el.email
      }));
    },
    async fetchGroupList() {
      const url = `group/tenant/${this.getTenantId}`;
      const { data } = await axios.get(url);
      this.groupOptions = data.map(el => ({
        id: el.id,
        name: el.title,
      }));
    },

    async refetchConfigurationValuesOptions() {
      if (!this.isValidMatrix || !this.matrixConfig.length) return;

      // Refetch field options for each configuration
      await Promise.all(
        this.matrixConfig.map(async (config) => {
          const relatedConfigurationProp = this.getRelatedConfigurationProp(config);
          const configId = relatedConfigurationProp?.data?.id;
          if (configId) {
            const fetchOptionsMethod = async () => await this.fetchFieldOptions(configId);
            await relatedConfigurationProp.fetchOptions(fetchOptionsMethod);

            const relatedFieldData = this.fieldOptions.find(field => field.id === configId);
            relatedConfigurationProp.setIsMulti(checkIsMulti(this.fieldTypesWithOptions, relatedFieldData));
          }
        })
      );
      this.updateTableKey(); // Update table key to refresh the table
    },

    async initOptions() {
      this.isValuesLoading = true;

      const promises = [
        this.getFieldsWithOptions(),
        this.fetchUserList(),
        this.fetchGroupList(),
      ];

      if (!this.isClient)
        promises.push(this.fetchClientList());

      await Promise.all(promises);
      await this.refetchConfigurationValuesOptions();
      this.isValuesLoading = false;
    },
    /* /OPTIONS */

    getOptionsByValue(value, options, trackBy = 'id') {
      if (!value || !options) return null;

      const getById = (val) => {
        const option = options.find(opt => opt[trackBy] === val[trackBy]);
        return option || val[trackBy];
      };

      if (Array.isArray(value)) {
        return value.map(getById);
      } else if (value && typeof value === 'object' && value[trackBy]) {
        return getById(value);
      } else if (typeof value === 'string') {
        return options.find(opt => opt[trackBy] === value) || value;
      }
    },
  },
};
</script>
<style lang="scss">
.matrix-edit-table--dropdown {
  min-width: 180px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
<style lang="scss" scoped>
.matrix-edit-table {
  ::v-deep {
    table.vgt-table td {
      padding: 1px 2px;
    }
  }
}
</style>